import { useEffect, useMemo, useState } from "react"
import { useUser } from "~/providers/userProvider"
import TopProductsCard from "~/components/collections/cards/TopProductsCard"
import {
  type ICollectionDetails,
  type ICollectionSimple,
  useGetCollections,
} from "~/hooks/useCollections"
import { filterNotUndefinedOrNull } from "~/utils/tsUtils"
import BottomProductsCard from "~/components/collections/cards/BottomProductsCard"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import CollectionSalesGraphCard from "~/components/collections/cards/CollectionSalesGraphCard"
import CollectionPreOrdersGraphCard from "../cards/CollectionPreOrderGraphCard"
import CollectionTable from "../CollectionTable"
import Select from "~/components/reusable/Select"
import { useSessionStorage } from "usehooks-ts"
import { maxBy, minBy, startCase } from "lodash"
import { AggregationMethod } from "../../reusable/AggregationSelector"
import CollectionsSelect from "~/components/collections/CollectionsSelect"
import { PerformanceLevel, CollectionTableColumn } from "~/constants/collections"
import { useProductTableAggregations } from "~/hooks/useProductTable"
import { useCompany } from "~/hooks/useCompany"
import { getNewAggregations, resolveDefaultPerformanceLevel } from "~/utils/productTableUtils"
import { Stack } from "@phosphor-icons/react"
import { api } from "~/utils/api"
import { useSkuStore } from "~/providers/sku-store-provider"
import { AnalysisPage } from "~/components/design-system/pages"
import ViewConfiguration from "~/components/planning/ViewConfiguration"
import CollectionInfoCard from "~/components/demand-plan/CollectionInfoCard"
import { type PresetView } from "~/components/planning/presetView"
import { useDemandPlanContext } from "~/context/DemandPlanProvider"

type Props = {
  collections: ICollectionDetails[]
  variants: IVariantSummary[]
  startDate: Date
  endDate: Date
  presetViews?: PresetView[]
  presetGroupId?: string
}

function getInitialAggregation(level: PerformanceLevel) {
  if (level === PerformanceLevel.ProductId) {
    return AggregationMethod.ProductName
  } else if (level === PerformanceLevel.VariantId) {
    return AggregationMethod.Variant
  }
  return startCase(level) as AggregationMethod
}

const CollectionProductsTab = ({
  collections,
  variants,
  startDate,
  endDate,
  presetViews,
  presetGroupId,
}: Props) => {
  const [userSelectedCollections, setUserSelectedCollections] = useState<
    ICollectionSimple[] | undefined
  >(undefined)
  const { data: company } = useCompany()
  const defaultLevel = resolveDefaultPerformanceLevel(company?.defaultAggregationLevel)
  const [level, setLevel] = useSessionStorage<PerformanceLevel>(
    "collection-products-tab-level",
    defaultLevel,
  )

  const collectionIds = collections.map((c) => c.id)
  const { demandPlanId } = useDemandPlanContext()
  const selectedChannel = useSkuStore((state) => state.channel)
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)

  const linkedCollectionIds = useMemo(
    () => filterNotUndefinedOrNull(collections.map((c) => c.predecessor?.id)),
    [collections],
  )
  const shouldFetchLinkedCollections = linkedCollectionIds.length > 0
  const { data: linkedComparisonCollections } = useGetCollections({
    collectionIds: shouldFetchLinkedCollections ? linkedCollectionIds : undefined,
  })

  const comparisonCollections = useMemo(() => {
    if (userSelectedCollections !== undefined) {
      return userSelectedCollections
    }
    if (shouldFetchLinkedCollections) {
      return linkedComparisonCollections ?? []
    }
    return []
  }, [userSelectedCollections, linkedComparisonCollections, shouldFetchLinkedCollections])

  const { data: products, isLoading } = api.preOrder.getTopBottomProductsForCollection.useQuery({
    collectionIds,
    level,
    warehouseId: selectedWarehouseId,
    channel: selectedChannel,
  })

  const { permissions } = useUser()

  const [, setAggregations] = useProductTableAggregations(
    "products-collection",
    getInitialAggregation(level),
  )

  const overallComparisonStartDate = minBy(comparisonCollections, "startDate")?.startDate
  const overallComparisonEndDate = maxBy(comparisonCollections, "endDate")?.endDate

  useEffect(() => {
    setAggregations(getNewAggregations(getInitialAggregation(level)))
  }, [level, setAggregations])

  const options = Object.values(PerformanceLevel).map((l) => ({
    label: startCase(l.replace("Id", "")),
    value: l,
  }))

  const viewConfiguration = (
    <ViewConfiguration
      tableId="products-collection"
      showFilters
      showWarehouseSelect
      showChannelSelect
      presetViews={presetViews}
      presetGroupId={presetGroupId}
      extraDisplayOptions={
        <>
          <div className="flex flex-row items-center justify-between gap-2">
            <span className="label-base">Level</span>
            <Select
              className="min-w-[135px]"
              value={options.find((o) => o.value === level)}
              onChange={(value) => setLevel(value?.value as PerformanceLevel)}
              options={options}
              size="sm"
              leftIcon={<Stack />}
            />
          </div>
          <div className="flex flex-row items-center justify-between gap-2">
            <span className="label-base">Compare to</span>
            <CollectionsSelect
              selectedCollectionIds={comparisonCollections.map((c) => c.id)}
              excludedCollectionIds={collectionIds}
              onCollectionSelectChange={setUserSelectedCollections}
              className="min-w-[185px]"
              isMulti
            />
          </div>
        </>
      }
    />
  )

  const inspector = (
    <>
      <CollectionInfoCard
        collections={collections}
        variants={variants}
        defaultExpanded={false}
        demandPlanId={demandPlanId}
      />
      <CollectionPreOrdersGraphCard
        collections={collections}
        comparisonCollections={comparisonCollections}
        startDate={startDate}
        endDate={endDate}
        comparisonStartDate={overallComparisonStartDate ?? undefined}
        comparisonEndDate={overallComparisonEndDate ?? undefined}
        warehouseId={selectedWarehouseId}
        channel={selectedChannel}
      />
      <CollectionSalesGraphCard
        collections={collections}
        comparisonCollections={comparisonCollections}
        startDate={startDate}
        endDate={endDate}
      />
      <TopProductsCard topProducts={products?.top ?? []} isLoading={isLoading} />
      <BottomProductsCard bottomProducts={products?.bottom ?? []} isLoading={isLoading} />
    </>
  )

  return (
    <AnalysisPage
      fillContent
      viewConfiguration={viewConfiguration}
      inspector={inspector}
      inspectorWidth="lg"
      inspectorClassName="h-full"
    >
      <CollectionTable
        id="products-collection"
        collections={collections}
        variants={variants}
        selectableRows={permissions.canEdit}
        overrideComparisonCollections={comparisonCollections}
        includeComparisonOnlyVariants
        autoShowComparisonColumns
        enableFilter
        initialSortColumn={{
          name: CollectionTableColumn.PreOrders,
          direction: "desc",
        }}
        warehouseId={selectedWarehouseId}
        channel={selectedChannel}
      />
    </AnalysisPage>
  )
}

export default CollectionProductsTab
